import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, MapPin, Clock, Plus, Edit, Trash2, Image as ImageIcon, Globe, Tag } from 'lucide-react';
import { getEvents, createEvent, updateEvent, deleteEvent } from '../../lib/api';
import toast from 'react-hot-toast';
import type { Event } from '../../lib/types';

interface EventFormData {
  title: string;
  description: string;
  longDescription?: string;
  attractions: string[];
  highlights: string[];
  date: string;
  location: string;
  image_url: string;
  status: 'draft' | 'published';
}

const initialFormData: EventFormData = {
  title: '',
  description: '',
  longDescription: '',
  attractions: [],
  highlights: [],
  date: new Date().toISOString().split('T')[0],
  location: '',
  image_url: '',
  status: 'draft'
};

export default function Events() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<EventFormData>(initialFormData);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const formatDateForInput = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const fetchEvents = useCallback(async () => {
    try {
      const data = await getEvents();
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingId) {
        await updateEvent(editingId, formData);
        toast.success('Evento aggiornato con successo');
      } else {
        await createEvent(formData);
        toast.success('Evento creato con successo');
      }

      setIsModalOpen(false);
      setFormData(initialFormData);
      setEditingId(null);
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error('Errore nel salvare l\'evento');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (event: Event) => {
    setFormData({
      title: event.title,
      description: event.description || '',
      longDescription: event.longDescription || '',
      attractions: event.attractions || [],
      highlights: event.highlights || [],
      date: formatDateForInput(event.date),
      location: event.location || '',
      image_url: event.image_url || '',
      status: event.status as 'draft' | 'published'
    });
    setEditingId(event.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteEvent(id);
        setEvents(events.filter(event => event.id !== id));
        toast.success('Event deleted successfully');
      } catch (error) {
        console.error('Error deleting event:', error);
        toast.error('Failed to delete event');
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">Event Management</h2>
        <button
          onClick={() => {
            setFormData(initialFormData);
            setEditingId(null);
            setIsModalOpen(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Event
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Event
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {events.map((event) => (
              <tr key={event.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-pink-600 mr-2" />
                    <div className="max-w-lg">
                      <div className="text-sm font-medium text-gray-900">
                        {event.title}
                      </div>
                      {event.description && (
                        <div className="text-sm text-gray-500 line-clamp-2">
                          {event.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-gray-400 mr-2" />
                    <div className="text-sm text-gray-500">
                      {new Date(event.date).toLocaleString('it-IT', {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                    <div className="text-sm text-gray-500">
                      {event.location || 'Non specificato'}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    event.status === 'published' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  } transition-colors duration-200`}>
                    {event.status}
                  </span>
                  {event.attractions && event.attractions.length > 0 && (
                    <div className="mt-1">
                      <div className="flex flex-wrap gap-1">
                        {event.attractions.map((attraction, idx) => (
                          <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-pink-100 text-pink-800 hover:bg-pink-200 transition-colors duration-200">
                            {attraction}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    className="text-pink-600 hover:text-pink-900 mr-3 p-1 hover:bg-pink-50 rounded-full transition-all duration-200"
                    onClick={() => handleEdit(event)}
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded-full transition-all duration-200"
                    onClick={() => handleDelete(event.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Form */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsModalOpen(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                        Titolo dell'evento
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Tag className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="title"
                          required
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
                          placeholder="Inserisci il titolo dell'evento"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Descrizione Breve
                        <span className="text-xs text-gray-500 ml-1">Breve descrizione per l'anteprima</span>
                      </label>
                      <div className="mt-1">
                        <textarea
                          id="description"
                          rows={3}
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
                          placeholder="Breve descrizione dell'evento (max 200 caratteri)..."
                          maxLength={200}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {formData.description?.length || 0}/200 caratteri
                        </p>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="longDescription" className="block text-sm font-medium text-gray-700">
                        Descrizione Dettagliata
                        <span className="text-xs text-gray-500 ml-1">Descrizione completa e dettagliata dell'evento</span>
                      </label>
                      <div className="mt-1">
                        <textarea
                          id="longDescription"
                          rows={6}
                          value={formData.longDescription || ''}
                          onChange={(e) => setFormData({ ...formData, longDescription: e.target.value || null })}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm resize-y"
                          placeholder="Descrivi in dettaglio l'evento, includi informazioni su attività, programma, e altri dettagli importanti..."
                        />
                        <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-md border border-gray-200">
                          Usa questo spazio per fornire tutti i dettagli dell'evento. Puoi includere:
                          <ul className="list-disc list-inside mt-1 ml-2">
                            <li>Programma dettagliato</li>
                            <li>Informazioni sulle attività</li>
                            <li>Dettagli logistici</li>
                            <li>Note importanti per i partecipanti</li>
                            <li>Regolamento e requisiti</li>
                            <li>Informazioni sui servizi disponibili</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="attractions" className="block text-sm font-medium text-gray-700">
                        Attrazioni (una per riga)
                        <span className="text-xs text-gray-500 ml-1">Giostre e attrazioni presenti</span>
                      </label>
                      <div className="mt-1">
                        <div className="p-2 border rounded-md border-gray-300 focus-within:border-pink-500 focus-within:ring-1 focus-within:ring-pink-500">
                          <div className="flex flex-wrap gap-2 mb-2">
                            {formData.attractions.map((attraction, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-pink-100 text-pink-800 hover:bg-pink-200 transition-all duration-200 shadow-sm hover:shadow"
                              >
                                {attraction}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newAttractions = [...formData.attractions];
                                    newAttractions.splice(index, 1);
                                    setFormData({ ...formData, attractions: newAttractions });
                                  }}
                                  className="ml-2 inline-flex items-center justify-center w-5 h-5 text-pink-400 hover:text-pink-600 hover:bg-pink-200 rounded-full transition-all duration-200"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          <input
                            type="text"
                            placeholder="Es: Tagada, Dischi Volanti..."
                            className="w-full border-0 p-0 focus:ring-0 text-sm"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const input = e.target as HTMLInputElement;
                                const value = input.value.trim();
                                if (value) {
                                  setFormData({
                                    ...formData,
                                    attractions: [...formData.attractions, value]
                                  });
                                  input.value = '';
                                }
                              }
                            }}
                          />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Aggiungi le giostre e le attrazioni che saranno presenti all'evento.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="highlights" className="block text-sm font-medium text-gray-700">
                        Highlights (uno per riga)
                        <span className="text-xs text-gray-500 ml-1">Punti salienti dell'evento</span>
                      </label>
                      <div className="mt-1">
                        <div className="p-2 border rounded-md border-gray-300 focus-within:border-pink-500 focus-within:ring-1 focus-within:ring-pink-500">
                          <div className="flex flex-wrap gap-2 mb-2">
                            {formData.highlights.map((highlight, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200 transition-all duration-200 shadow-sm hover:shadow"
                              >
                                {highlight}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newHighlights = [...formData.highlights];
                                    newHighlights.splice(index, 1);
                                    setFormData({ ...formData, highlights: newHighlights });
                                  }}
                                  className="ml-2 inline-flex items-center justify-center w-5 h-5 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-200 rounded-full transition-all duration-200"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          <input
                            type="text"
                            placeholder="Es: Spettacolo di luci, Area food & beverage..."
                            className="w-full border-0 p-0 focus:ring-0 text-sm"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const input = e.target as HTMLInputElement;
                                const value = input.value.trim();
                                if (value) {
                                  setFormData({
                                    ...formData,
                                    highlights: [...formData.highlights, value]
                                  });
                                  input.value = '';
                                }
                              }
                            }}
                          />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Aggiungi i punti salienti dell'evento. Ogni highlight dovrebbe essere una caratteristica o un'attrazione speciale.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                        Data e Ora
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Calendar className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="datetime-local"
                          id="date"
                          required
                          value={formData.date}
                          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                          className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                        Luogo
                        <span className="text-xs text-gray-500 ml-1">Indirizzo completo dell'evento</span>
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Globe className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="location"
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
                          placeholder="Es: Via Roma 123, Milano, MI"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="image_url" className="block text-sm font-medium text-gray-700">
                        URL Immagine
                        <span className="text-xs text-gray-500 ml-1">Link diretto all'immagine</span>
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <ImageIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="url"
                          id="image_url"
                          value={formData.image_url}
                          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                          className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
                          placeholder="https://..."
                        />
                      </div>
                      {formData.image_url && (
                        <div className="mt-2">
                          <img
                            src={formData.image_url}
                            alt="Preview"
                            className="h-32 w-full object-cover rounded-lg"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://via.placeholder.com/400x300?text=Invalid+Image+URL';
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                        Stato
                        <span className="text-xs text-gray-500 ml-1">Bozza = visibile solo admin, Pubblicato = visibile a tutti</span>
                      </label>
                      <select
                        id="status"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as 'draft' | 'published' })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
                      >
                        <option value="draft">🔒 Bozza</option>
                        <option value="published">🌍 Pubblicato</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-pink-600 text-base font-medium text-white hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                  >
                    {submitting ? 'Salvataggio...' : editingId ? 'Aggiorna' : 'Crea'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Annulla
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}